import { useMutation, useQuery } from "convex/react";
import {
	ArrowLeftIcon,
	GitBranchIcon,
	LogOutIcon,
	MoonIcon,
	PencilIcon,
	PlusIcon,
	SearchIcon,
	SunIcon,
	TrashIcon,
} from "lucide-react";
import { useState } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router";
import AllThreadsSection from "@/components/chat/AllThreadsSection";
import LogoIcon from "@/components/LogoIcon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarRail,
} from "@/components/ui/sidebar";
import { useDarkMode } from "@/hooks/use-dark-mode";
import { authClient } from "@/lib/authClient";
import { BUCKET_ORDER, bucketFor } from "@/lib/dateBuckets";
import { cn } from "@/lib/utils";
import { api } from "../../../convex/_generated/api";

type ChatThread = {
	_id: string;
	_creationTime: number;
	clericalThreadId: string;
	title?: string;
	parentClericalThreadId?: string;
	awaitingInput?: boolean;
};

function getInitials(value: string) {
	return value
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? "")
		.join("");
}

function ThreadRowMenu({
	thread,
	onRename,
	onDelete,
}: {
	thread: ChatThread;
	onRename: (t: ChatThread) => void;
	onDelete: (t: ChatThread) => void;
}) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				render={
					<SidebarMenuAction showOnHover>
						<span className="sr-only">Thread actions</span>
						<svg
							viewBox="0 0 16 16"
							className="size-3.5"
							aria-hidden
							fill="currentColor"
						>
							<title>More</title>
							<circle cx="3" cy="8" r="1.4" />
							<circle cx="8" cy="8" r="1.4" />
							<circle cx="13" cy="8" r="1.4" />
						</svg>
					</SidebarMenuAction>
				}
			/>
			<DropdownMenuContent side="right" align="start" className="w-44">
				<DropdownMenuItem onClick={() => onRename(thread)}>
					<PencilIcon className="size-3.5" />
					Rename
				</DropdownMenuItem>
				<DropdownMenuItem
					onClick={() => onDelete(thread)}
					className="text-destructive focus:text-destructive"
				>
					<TrashIcon className="size-3.5" />
					Delete
				</DropdownMenuItem>
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

export default function ChatSidebar({
	onOpenSearch,
}: {
	onOpenSearch: () => void;
}) {
	const { threadId: activeThreadId } = useParams<{ threadId?: string }>();
	const navigate = useNavigate();
	const { isDark, toggle: toggleDarkMode } = useDarkMode();
	const session = authClient.useSession();
	const user = session.data?.user as
		| { name?: string; email?: string; image?: string }
		| undefined;

	const threads = useQuery(api.agent.queries.listChatThreads) ?? [];
	const isStaff = useQuery(api.staff.isCurrentUserStaff);
	const renameMutation = useMutation(api.agent.queries.renameChatThread);
	const deleteMutation = useMutation(api.agent.queries.deleteChatThread);

	const [renamingId, setRenamingId] = useState<string | null>(null);

	const grouped = new Map<string, ChatThread[]>();
	for (const t of threads) {
		const bucket = bucketFor(t._creationTime);
		const arr = grouped.get(bucket) ?? [];
		arr.push(t);
		grouped.set(bucket, arr);
	}

	const userName = user?.name?.trim() || user?.email || "Clerical user";
	const userEmail = user?.email || "Workspace";

	function handleSignOut() {
		void authClient.signOut();
		navigate("/", { replace: true });
	}

	function handleDelete(t: ChatThread) {
		const ok = window.confirm(
			`Delete "${t.title ?? "this chat"}"? This cannot be undone.`,
		);
		if (!ok) return;
		void deleteMutation({ clericalThreadId: t.clericalThreadId }).then(() => {
			if (activeThreadId === t.clericalThreadId) {
				navigate("/chat", { replace: true });
			}
		});
	}

	return (
		<Sidebar collapsible="icon">
			<SidebarHeader>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton
							size="lg"
							render={<Link to="/chat" />}
							tooltip="Clerical Chat"
						>
							<div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
								<LogoIcon className="size-5" />
							</div>
							<span className="truncate font-semibold">Clerical Chat</span>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton
							onClick={onOpenSearch}
							tooltip="Search chats (⌘K)"
						>
							<SearchIcon />
							<span>Search</span>
							<KbdGroup className="ml-auto group-data-[collapsible=icon]:hidden">
								<Kbd>⌘</Kbd>
								<Kbd>K</Kbd>
							</KbdGroup>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton
							onClick={() => navigate("/chat")}
							tooltip="New chat"
							className="bg-primary font-medium text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
						>
							<PlusIcon />
							<span>New chat</span>
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarHeader>
			<SidebarContent>
				{BUCKET_ORDER.filter((b) => grouped.has(b)).map((bucket) => (
					<SidebarGroup
						key={bucket}
						className="group-data-[collapsible=icon]:hidden"
					>
						<SidebarGroupLabel>{bucket}</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								{grouped.get(bucket)?.map((t) => (
									<SidebarMenuItem key={t._id}>
										{renamingId === t.clericalThreadId ? (
											<form
												className="flex h-8 items-center gap-1 px-2"
												onSubmit={(e) => {
													e.preventDefault();
													const input = e.currentTarget.querySelector(
														"input",
													) as HTMLInputElement | null;
													const title = input?.value.trim() ?? "";
													if (title) {
														void renameMutation({
															clericalThreadId: t.clericalThreadId,
															title,
														});
													}
													setRenamingId(null);
												}}
											>
												<input
													ref={(node) => {
														node?.focus();
														node?.select();
													}}
													defaultValue={t.title ?? ""}
													onBlur={() => setRenamingId(null)}
													onKeyDown={(e) => {
														if (e.key === "Escape") setRenamingId(null);
													}}
													className="flex-1 rounded border bg-background px-1.5 text-xs outline-none ring-ring focus:ring-1"
												/>
											</form>
										) : (
											<>
												<SidebarMenuButton
													render={
														<NavLink
															to={`/chat/${encodeURIComponent(
																t.clericalThreadId,
															)}`}
														/>
													}
													isActive={activeThreadId === t.clericalThreadId}
													tooltip={t.title ?? t.clericalThreadId}
												>
													{t.parentClericalThreadId ? (
														<GitBranchIcon className="size-3.5 shrink-0 text-muted-foreground" />
													) : null}
													<span className="truncate">
														{t.title ?? "Untitled chat"}
													</span>
													{t.awaitingInput ? (
														<span
															role="img"
															aria-label="Needs your input"
															title="Needs your input"
															className="ml-auto size-2 shrink-0 rounded-full bg-primary"
														/>
													) : null}
												</SidebarMenuButton>
												<ThreadRowMenu
													thread={t}
													onRename={(thr) =>
														setRenamingId(thr.clericalThreadId)
													}
													onDelete={handleDelete}
												/>
											</>
										)}
									</SidebarMenuItem>
								))}
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>
				))}
				{threads.length === 0 ? (
					<div className="px-4 py-6 text-center text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
						No chats yet — start one with the New chat button above.
					</div>
				) : null}
				{isStaff === true ? (
					<AllThreadsSection activeThreadId={activeThreadId} />
				) : null}
			</SidebarContent>
			<SidebarFooter>
				<div className="px-2 group-data-[collapsible=icon]:hidden">
					<Button
						variant="ghost"
						size="sm"
						className="w-full justify-start gap-2 text-xs text-sidebar-foreground/70 hover:text-sidebar-foreground"
						onClick={() => navigate("/workspace")}
					>
						<ArrowLeftIcon className="size-3.5" />
						Back to workspace
					</Button>
				</div>
				<div
					className={cn(
						"flex items-center gap-2 rounded-lg border border-sidebar-border/70 bg-sidebar-accent/30 p-2",
						"group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:border-transparent group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:p-0",
					)}
				>
					<Avatar>
						<AvatarImage src={user?.image ?? ""} alt={userName} />
						<AvatarFallback>{getInitials(userName)}</AvatarFallback>
					</Avatar>
					<div className="grid min-w-0 flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
						<span className="truncate font-medium">{userName}</span>
						<span className="truncate text-xs text-sidebar-foreground/70">
							{userEmail}
						</span>
					</div>
					<Button
						variant="ghost"
						size="icon"
						onClick={toggleDarkMode}
						title={isDark ? "Switch to light mode" : "Switch to dark mode"}
						className="shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:hidden"
					>
						{isDark ? (
							<SunIcon className="size-4" />
						) : (
							<MoonIcon className="size-4" />
						)}
					</Button>
					<Button
						variant="ghost"
						size="icon"
						onClick={handleSignOut}
						title="Sign out"
						className="shrink-0 text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground group-data-[collapsible=icon]:hidden"
					>
						<LogOutIcon className="size-4" />
					</Button>
				</div>
			</SidebarFooter>
			<SidebarRail />
		</Sidebar>
	);
}
